import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/auth";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

import HeroSection from "@/components/sections/HeroSection";
import CategoriesSection from "@/components/sections/CategoriesSection";
import FeaturedProducts, { FeaturedProductsSkeleton } from "@/components/sections/FeaturedProducts";
import AboutSection from "@/components/sections/AboutSection";
import HomeVideoSection from "@/components/home/HomeVideoSection";

import { SEOHead } from "@/components/SEO/SEOHead";
import { Home } from "lucide-react";
import TrustPilotWidget from "@/components/common/TrustPilotWidget";
import { useFeaturedProducts } from "@/hooks/useFeaturedProducts";
import { productsPrefetchHandlers } from "@/utils/prefetchProductsRoute";

const Index = () => {
  const { user, isAdmin, isSuperAdmin, userRoles, loading } = useAuth();

  // Same queryKey as the hook call inside FeaturedProducts.tsx, so React Query shares this
  // fetch rather than duplicating it — this call just warms the cache for logged-in users too,
  // who see the CTA instead of the grid below; that's an acceptable trade-off since we can't
  // know which branch we'll need until `loading` resolves. Query is delayed via
  // featuredProductsEnabled below; first paint is unaffected because useFeaturedProducts ships
  // placeholderData, so <FeaturedProducts/> renders real (snapshot) cards immediately either way.
  const [featuredProductsEnabled, setFeaturedProductsEnabled] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setFeaturedProductsEnabled(true), 1200);
    return () => window.clearTimeout(timer);
  }, []);
  useFeaturedProducts({ enabled: featuredProductsEnabled });

  // HomeVideoSection's home_videos query fires immediately once enabled — delay it
  // past the first second so it doesn't compete with the initial load burst above.
  // The video element itself won't render for 7s-or-scroll regardless (see
  // HomeVideoSection's internal gate), so this delay is invisible to the user.
  const [homeVideoEnabled, setHomeVideoEnabled] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setHomeVideoEnabled(true), 2000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <>
      <SEOHead
        title="Koöperasie Veevoer Winkel | Premium Agricultural Supplies"
        description="Your trusted agricultural supplier in South Africa. Quality feed, farm supplies, hardware, and pet products. Fast delivery nationwide."
        keywords="agricultural supplies, animal feed, farm equipment, south africa, veevoer"
        url="https://veevoerwinkel.co.za/"
      />

      <div className="min-h-screen flex flex-col">
        <Header />

        {/* Admin Quick Access Panel - For Admin Personnel */}
        {user && (isAdmin || isSuperAdmin) && (
          <div className="bg-springbok-800 text-white px-4 py-2.5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 text-sm">
              <span className="font-semibold text-amber-400">⚙ Admin</span>
              <span className="text-springbok-300 hidden sm:inline">{user.email}</span>
              {isSuperAdmin && (
                <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  Super Admin
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/admin/orders">
                <Button size="sm" variant="ghost" className="text-white hover:bg-springbok-700 h-7 text-xs">
                  Orders
                </Button>
              </Link>
              <Link to="/admin/products">
                <Button size="sm" variant="ghost" className="text-white hover:bg-springbok-700 h-7 text-xs">
                  Products
                </Button>
              </Link>
              <Link to="/admin/blog-management">
                <Button size="sm" variant="ghost" className="text-white hover:bg-springbok-700 h-7 text-xs">
                  Blog
                </Button>
              </Link>
              <Link to="/admin">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white h-7 text-xs font-semibold">
                  Admin Panel →
                </Button>
              </Link>
            </div>
          </div>
        )}

        <main className="flex-grow">
          {/* Hero is pure static/public content — render it optimistically (assume
              logged-out, the common case) instead of waiting on the async auth check
              before first paint. `user` goes truthy synchronously the instant
              useAuthState's onAuthStateChange callback fires (before `loading` clears —
              that also waits on a roles fetch), so gating on `!user` alone hides Hero
              as soon as we know about a session, without waiting on anything extra.
              Once auth resolves, hide it if the visitor turns out to be logged in
              (brief flash-then-collapse, same collapse that already happened here
              before — this just removes the network wait beforehand). */}
          {!user && <HeroSection />}

          <CategoriesSection />

          {/* Wait for auth to load before deciding which to show; reserve space with the same skeleton FeaturedProducts uses internally so mounting it causes no visible shift */}
          {loading ? (
            <FeaturedProductsSkeleton />
          ) : user ? (
            <div className="bg-cream-50 py-8">
              <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h2 className="text-xl font-semibold text-springbok-800 mb-2">What are you looking for today?</h2>
                <p className="text-gray-500 text-sm mb-6">Browse all our products or shop by category above.</p>
                <Link to="/products" {...productsPrefetchHandlers}>
                  <Button className="bg-springbok-500 hover:bg-springbok-600 text-white px-10 py-6 text-base font-semibold rounded-lg shadow">
                    Browse All Products
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <FeaturedProducts enabled={featuredProductsEnabled} />
          )}

          {/* HomeVideoSection mounts immediately, but its DB query is gated by homeVideoEnabled
              (see above) so it doesn't fire in the first second; internal 7s-or-scroll delay
              separately handles when the actual <video> element loads */}
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ minHeight: "256px" }}>
            <HomeVideoSection enabled={homeVideoEnabled} />
          </div>

          {/* TrustPilot — wrapper always renders to prevent CLS */}
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" style={{ minHeight: "80px" }}>
            {!user && !loading && <TrustPilotWidget />}
          </div>

          {/* About section only for logged-out visitors */}
          {!user && !loading && <AboutSection />}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Index;
